import { useState } from "react";
import Card from "components/Card";
import InputField from "components/InputField";
import Button from "components/Button";
import { postManagerDeclineLeave, postManagerApproveLeave, postApproveLeave, postDeclineLeave, postClientUserActionLeave } from "api/apiHelper";
import { LeaveDetailsType } from "pages/leaves/Leaves";
import { toast } from "react-toastify";
import { useIsManager, usePermissions, useIsClientUser } from "app/hooks";

interface ApproveLeaveFormPropsType {
  leaveId: string;
  leaveDetails: LeaveDetailsType | null; 
  updateLeaveDetails?: (leaveId: string) => void;
}

const ApproveLeaveForm = (props: ApproveLeaveFormPropsType) => {

  const { leaveId, leaveDetails, updateLeaveDetails } = props;
  const [comments, setComments] = useState("");
  const permissions = usePermissions() || [];
  const isClientUser = useIsClientUser();

  const isManager = useIsManager() || false;
  const isHR = permissions.includes('leaves.approve_employeeleavesdetails');

  // Don't show form if no permissions or if already fully approved/declined
  const leaveStatus = leaveDetails?.leave_status as string | undefined;
  
  // For client users, hide form if they've already acted (CA or CR) or if fully approved/declined
  if (isClientUser && (leaveStatus === 'A' || leaveStatus === 'D' || leaveStatus === 'CR' || leaveStatus === 'CA')) {
    return null;
  }
  
  // For managers and HR, hide form if fully approved/declined
  if ((!isManager && !isHR && !isClientUser) || 
      (leaveStatus === 'A' || leaveStatus === 'D')) {
    return null;
  }

  // Determine if current user can act on this leave based on role and status
  const canManagerAct = () => {
    return isManager && 
           leaveDetails?.leave_status === 'P'
  };

  const canHRAct = () => {
    return isHR && 
           (leaveDetails?.leave_status === 'P' || leaveDetails?.leave_status === 'MA' || leaveDetails?.leave_status === 'CA')
  };

  const canClientUserAct = () => {
    if (!isClientUser || !leaveDetails) return false;
    const status = leaveDetails.leave_status as string;
    // Client users can only act on MA (Manager Approved) status
    // Once they approve (CA) or reject (CR), they cannot act again
    return status === 'MA';
  };

  const canShowButtons = () => {
    return canManagerAct() || canHRAct() || canClientUserAct();
  };

  const handleApprove = async () => {
    if (!leaveDetails) return;

    // Manager approval for Pending leaves
    if (canManagerAct()) {
      const notification = toast.loading("Manager Approving Leave...");

      try {
        await postManagerApproveLeave(leaveId, comments);
        toast.update(notification, {
          render: "Leave Manager Approved successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error: any) {
        console.log(error);

        let errorMessage = "Failed to approve leave";
        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (errorMessage.length > 80) {
            errorMessage = "Failed to approve leave";
          }
        }
        toast.update(notification, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }

    // HR final approval for Pending or Manager Approved leaves
    if (canHRAct()) {
      const notification = toast.loading("Final Approval in Progress...");

      try {
        await postApproveLeave(leaveId, comments);
        toast.update(notification, {
          render: "Leave Approved successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error:any) {
        console.log(error);
        
        let errorMessage = "Failed to approve leave";
        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (errorMessage.length > 80) {
            errorMessage = "Failed to approve leave";
          }
        }
        toast.update(notification, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }

    // Client user approval for Manager Approved or Client Approved leaves
    if (canClientUserAct()) {
      const notification = toast.loading("Processing Leave Approval...");

      try {
        await postClientUserActionLeave(leaveId, 'approve', comments);
        toast.update(notification, {
          render: "Leave Approved successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error: any) {
        console.log(error);
        
        let errorMessage = "Failed to approve leave";
        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (errorMessage.length > 80) {
            errorMessage = "Failed to approve leave";
          }
        }
        toast.update(notification, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }

    // If neither condition is met, show error
    toast.error("You cannot approve this leave at this stage");
  };

  const handleDecline = async () => {
    if (!leaveDetails) return;

    // Manager decline for Pending leaves
    if (canManagerAct()) {
      const notification = toast.loading("Manager Declining Leave...");

      try {
        await postManagerDeclineLeave(leaveId, comments);
        toast.update(notification, {
          render: "Leave Manager Declined successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error) {
        console.log(error);
        toast.update(notification, {
          render: "Failed to decline Leave",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }


    if (canHRAct()) {
      const notification = toast.loading("Final Decline in Progress...");

      try {
        await postDeclineLeave(leaveId, comments);
        toast.update(notification, {
          render: "Leave Declined successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error) {
        console.log(error);
        toast.update(notification, {
          render: "Failed to decline Leave",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }

    // Client user rejection for Manager Approved or Client Approved leaves
    if (canClientUserAct()) {
      const notification = toast.loading("Processing Leave Rejection...");

      try {
        await postClientUserActionLeave(leaveId, 'reject', comments);
        toast.update(notification, {
          render: "Leave Rejected successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        updateLeaveDetails?.(leaveId);
      } catch (error: any) {
        console.log(error);
        
        let errorMessage = "Failed to reject leave";
        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
          if (errorMessage.length > 80) {
            errorMessage = "Failed to reject leave";
          }
        }
        toast.update(notification, {
          render: errorMessage,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
      return;
    }

    // If neither condition is met, show error
    toast.error("You cannot decline this leave at this stage");
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComments(e.target.value);
  };

  // Get the appropriate title based on user role and leave status
  const getTitle = () => {
    if (canManagerAct()) {
      return 'Manager Review';
    }
    if (canHRAct()) {
      return 'HR Review';
    }
    if (canClientUserAct()) {
      return 'Client Review';
    }
    return 'Leave Review';
  };

  // Get button labels based on current stage
  const getButtonLabels = () => {
    if (canManagerAct()) {
      return {
        approve: 'Manager Approve',
        decline: 'Manager Decline'
      };
    }
    if (canHRAct()) {
      return {
        approve: 'Final Approve',
        decline: 'Final Decline'
      };
    }
    if (canClientUserAct()) {
      return {
        approve: 'Approve',
        decline: 'Reject'
      };
    }
    return {
      approve: 'Approve',
      decline: 'Decline'
    };
  };

  return (
    <Card extra={"w-full h-full pb-10 p-4"}>
      <div className="mb-8 mt-2 w-full">
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
          {getTitle()}
        </h4>
      </div>

      <div>
        <form className="flex gap-2 w-full">
          <InputField
            id="comments"
            type="text"
            placeholder="Enter comments here..."
            extra="w-full"
            variant="outlined"
            onChange={handleCommentsChange}
            value={comments}
            required
          />

          {canShowButtons() && (
            <>
              <Button
                type="button"
                varient="danger"
                onClick={handleDecline}
                disabled={!canShowButtons()}
              >
                {getButtonLabels().decline}
              </Button>
              <Button
                type="button"
                varient="success"
                onClick={handleApprove}
                disabled={!canShowButtons()}
              >
                {getButtonLabels().approve}
              </Button>
            </>
          )}
        </form>
      </div>
    </Card>
  );
};

export default ApproveLeaveForm;